import React, { useMemo } from 'react';
import {
  Drawer,
  List,
  Badge,
  Typography,
  Button,
  Space,
  Tag,
  Tooltip,
  Empty,
  Avatar,
  Dropdown,
  MenuProps,
  Spin,
  Alert
} from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  MoreOutlined,
  EyeOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useTheme } from '../context/ThemeContext';
import { useExecutionStageNotifications, Notification } from '../context/ExecutionStageNotificationContext';
import { useNavigate } from 'react-router-dom';

const { Text, Paragraph } = Typography;

interface ExecutionStageNotificationPanelProps {
  visible: boolean;
  onClose: () => void;
}

const ExecutionStageNotificationPanel: React.FC<ExecutionStageNotificationPanelProps> = ({ visible, onClose }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    refetchNotifications,
  } = useExecutionStageNotifications();

  const isDarkMode = theme.algorithm?.[0]?.toString().includes('dark');

  const getNotificationIcon = (type: Notification['type']) => {
    const iconProps = { style: { fontSize: '16px' } };
    switch (type) {
      case 'success':
        return <CheckCircleOutlined {...iconProps} style={{ color: '#52c41a' }} />;
      case 'error':
        return <ExclamationCircleOutlined {...iconProps} style={{ color: '#ff4d4f' }} />;
      case 'warning':
        return <WarningOutlined {...iconProps} style={{ color: '#faad14' }} />;
      case 'info':
      default:
        return <InfoCircleOutlined {...iconProps} style={{ color: '#1890ff' }} />;
    }
  };

  const getPriorityColor = (priority: Notification['priority']) => {
    switch (priority) {
      case 'critical':
        return '#ff4d4f';
      case 'high':
        return '#fa8c16';
      case 'medium':
        return '#faad14';
      case 'low':
      default:
        return '#52c41a';
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return timestamp.toLocaleDateString();
  };

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      // Sort unread first, then by timestamp (newest first)
      if (a.read !== b.read) {
        return a.read ? 1 : -1;
      }
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }, [notifications]);

  const handleNotificationClick = async (notification: Notification) => {
    try {
      if (!notification.read) {
        await markAsRead(notification.id);
      }

      if (notification.actionUrl) {
        // If it's an internal route, use navigate
        if (notification.actionUrl.startsWith('/')) {
          onClose();
          navigate(notification.actionUrl);
        } else {
          // External URL
          window.open(notification.actionUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const getDropdownItems = (notification: Notification): MenuProps['items'] => [
    {
      key: 'toggle-read',
      label: notification.read ? 'Mark as unread' : 'Mark as read',
      icon: notification.read ? <EyeOutlined /> : <CheckOutlined />,
      onClick: () => notification.read ? markAsUnread(notification.id) : markAsRead(notification.id),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => deleteNotification(notification.id),
    },
  ];

  const headerActions = (
    <Space>
      <Tooltip title="Refresh notifications">
        <Button
          type="text"
          icon={<ReloadOutlined />}
          onClick={refetchNotifications}
          loading={isLoading}
        />
      </Tooltip>
      {notifications.length > 0 && (
        <Tooltip title="Mark all as read">
          <Button
            type="text"
            icon={<CheckOutlined />}
            onClick={markAllAsRead}
            disabled={unreadCount === 0}
          />
        </Tooltip>
      )}
      {notifications.length > 0 && (
        <Tooltip title="Clear all notifications">
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={clearAllNotifications}
            danger
          />
        </Tooltip>
      )}
    </Space>
  );

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Badge count={unreadCount} size="small">
              <BellOutlined />
            </Badge>
            <span>Execution Stage Notifications</span>
          </Space>
          {headerActions}
        </div>
      }
      placement="right"
      width={480}
      open={visible}
      onClose={onClose}
      styles={{
        body: { padding: 0 },
        header: { borderBottom: `1px solid ${isDarkMode ? '#303030' : '#f0f0f0'}` }
      }}
      className="notification-drawer"
    >
      {error && (
        <Alert
          message="Error loading notifications"
          description={error}
          type="error"
          showIcon
          style={{ margin: '16px' }}
          action={
            <Button size="small" onClick={refetchNotifications}>
              Retry
            </Button>
          }
        />
      )}

      {isLoading && notifications.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Spin size="large" tip="Loading notifications..." />
        </div>
      ) : notifications.length === 0 ? (
        <div style={{ padding: '40px' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                No execution stage notifications yet
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  You'll receive notifications for execution stage activities here
                </Text>
              </span>
            }
          />
        </div>
      ) : (
        <List
          dataSource={sortedNotifications}
          renderItem={(notification) => (
            <List.Item
              style={{
                padding: '16px',
                borderBottom: `1px solid ${isDarkMode ? '#303030' : '#f0f0f0'}`,
                backgroundColor: notification.read ? 'transparent' : (isDarkMode ? 'rgba(24, 144, 255, 0.05)' : 'rgba(24, 144, 255, 0.03)'),
                cursor: notification.actionUrl ? 'pointer' : 'default',
                opacity: notification.read ? 0.7 : 1,
              }}
              onClick={() => handleNotificationClick(notification)}
              actions={[
                <Dropdown
                  menu={{ items: getDropdownItems(notification) }}
                  trigger={['click']}
                  placement="bottomLeft"
                  key="actions"
                >
                  <Button type="text" icon={<MoreOutlined />} size="small" />
                </Dropdown>
              ]}
            >
              <List.Item.Meta
                avatar={
                  <div style={{ position: 'relative' }}>
                    <Avatar
                      icon={getNotificationIcon(notification.type)}
                      style={{
                        backgroundColor: 'transparent',
                        border: `2px solid ${isDarkMode ? '#303030' : '#f0f0f0'}`,
                      }}
                    />
                    {!notification.read && (
                      <div
                        style={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#1890ff',
                        }}
                      />
                    )}
                  </div>
                }
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Text strong style={{ fontSize: '14px', lineHeight: '1.4' }}>
                      {notification.title}
                    </Text>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                        {formatTimestamp(notification.timestamp)}
                      </Text>
                      <Space size={4} style={{ marginTop: '4px' }}>
                        {notification.priority && (
                          <Tag
                            color={getPriorityColor(notification.priority)}
                            style={{ fontSize: '10px', lineHeight: '14px', margin: 0 }}
                          >
                            {notification.priority.toUpperCase()}
                          </Tag>
                        )}
                        {notification.category && (
                          <Tag style={{ fontSize: '10px', lineHeight: '14px', margin: 0 }}>
                            {notification.category.toUpperCase()}
                          </Tag>
                        )}
                      </Space>
                    </div>
                  </div>
                }
                description={
                  <div>
                    <Paragraph
                      style={{
                        margin: '4px 0 8px 0',
                        fontSize: '13px',
                        lineHeight: '1.4',
                        color: isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)'
                      }}
                      ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
                    >
                      {notification.message}
                    </Paragraph>
                    
                    {notification.sender && (
                      <div style={{ marginTop: '8px' }}>
                        <Text
                          type="secondary"
                          style={{
                            fontSize: '11px',
                            fontStyle: 'italic'
                          }}
                        >
                          From: {notification.sender.name}
                        </Text>
                      </div>
                    )}

                    {notification.actionText && notification.actionUrl && (
                      <div style={{ marginTop: '8px' }}>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                          icon={notification.actionUrl.startsWith('/') ? <FileTextOutlined /> : undefined}
                        >
                          {notification.actionText}
                        </Button>
                      </div>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
};

export default ExecutionStageNotificationPanel;
